import React, {useCallback, useMemo, useState} from 'react';
import {Box, Dialog as MuiDialog, DialogActions, DialogContent, DialogTitle} from '@mui/material';
import Button from '../../../../../../../components/base/button/button';
import Form, {MuiRFF} from '../../../../../../../components/form';
import Grid2 from '@mui/material/Unstable_Grid2';
import * as Yup from 'yup';
import ValidateMessages from '../../../../../../../../core/models/static/texts/vallidate-messages';
import useIsMounted from '../../../../../../../hooks/use-is-mounted';

export interface EditDashboardWidgetTitleDialogProps {
    open: boolean;
    title?: string;
    onSave?: (title: string) => Promise<boolean>;
    onClose?: () => void;
}

const formNames = {
    title: 'title'
};

const schema = Yup.object().shape({
    [formNames.title]: Yup.string().trim().required(ValidateMessages.required)
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const EditDashboardWidgetTitleDialog = ({open, onClose, title, onSave}: EditDashboardWidgetTitleDialogProps) => {
    const [editing, setEditing] = useState(false);
    const isMounted = useIsMounted();

    const initialValues = useMemo(() => ({
        [formNames.title]: title || '',
    }), [title]);

    /**
     * Handles creating a new dashboard
     */
    const handleSubmit = useCallback(async (values: any) => {
        if (title === values[formNames.title]) {
            onClose?.();
            return;
        }
        setEditing(true);
        const resultFlag = await onSave?.(values[formNames.title]);
        if (!isMounted())
            return;
        setEditing(false);
        if (resultFlag) {
            onClose?.();
        }
    }, [isMounted, onClose, onSave, title]);

    /**
     * Handles closing the dialog without saving
     */
    const handleClose = useCallback(() => {
        onClose?.();
    }, [onClose]);

    return (
        <MuiDialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle>Update Widget Title</DialogTitle>
            <Form
                onSubmit={handleSubmit}
                validate={validate}
                initialValues={initialValues}
                formProps={{id: 'edit-dashboard-widget-title-form'}}
            >
                <>
                    <DialogContent dividers>
                        <Grid2 container spacing={2}>
                            <Grid2 xs={12}>
                                <MuiRFF.TextField
                                    name={formNames.title}
                                    label="Widget Title"
                                    required={required[formNames.title]}
                                    fullWidth
                                />
                            </Grid2>
                        </Grid2>
                    </DialogContent>
                    <DialogActions className="p-0">
                        <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                            <Button
                                disabled={editing}
                                variant="dialog"
                                color="primary"
                                onClick={handleClose}
                                sx={{borderRadius: '0 10px 0 10px'}}
                            >
                                Close
                            </Button>
                            <Button
                                disabled={editing}
                                variant="dialog"
                                color="primary"
                                type="submit"
                                form="edit-dashboard-widget-title-form"
                                sx={{borderRadius: '10px 0 10px 0'}}
                            >
                                {editing ? "Updating..." : "Update"}
                            </Button>
                        </Box>
                    </DialogActions>
                </>
            </Form>
        </MuiDialog>
    );
};

export default EditDashboardWidgetTitleDialog;